import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Gift, Package, UsersThree, Storefront, Warning, ArrowClockwise,
  MagnifyingGlass, CheckSquare, UserPlus, ShoppingBag, Siren, ArrowDown,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

export default function VoucherStockPage() {
  const [stats, setStats] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('available');
  const [filterStaff, setFilterStaff] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [search, setSearch] = useState('');

  // Row-level assignment state: { [voucherId]: {id, name} }
  const [rowAssignments, setRowAssignments] = useState({});
  // Selection state for bulk actions
  const [selected, setSelected] = useState(new Set());

  // Staff list for autocomplete
  const [staffList, setStaffList] = useState([]);
  const [distributorList, setDistributorList] = useState([]);

  // Bulk assign dialog
  const [showAssignDlg, setShowAssignDlg] = useState(false);
  const [assignStaff, setAssignStaff] = useState(null);
  const [assignStaffInput, setAssignStaffInput] = useState('');

  // Sell dialog
  const [showSellDlg, setShowSellDlg] = useState(false);
  const [sellForm, setSellForm] = useState({ sold_to_name: '', sold_to_distributor_id: '' });

  // Complaint dialog
  const [showComplaintDlg, setShowComplaintDlg] = useState(false);
  const [complaintVoucher, setComplaintVoucher] = useState(null);
  const [complaintForm, setComplaintForm] = useState({ complaint_type: 'used', details: '' });

  // Admin-direct upload dialog
  const [showAdminDirectDlg, setShowAdminDirectDlg] = useState(false);
  const [adText, setAdText] = useState('');
  const [adMode, setAdMode] = useState('grouped');  // 'grouped' | 'line'
  const [adFile, setAdFile] = useState(null);
  const [adBusy, setAdBusy] = useState(false);

  const submitAdminDirect = async () => {
    if (!adText.trim() && !adFile) {
      toast.error('Text ya Excel file daalo');
      return;
    }
    setAdBusy(true);
    try {
      const fd = new FormData();
      if (adFile) fd.append('file', adFile);
      else { fd.append('text', adText); fd.append('mode', adMode); }
      const r = await api.post('/voucher-stock/admin-direct-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(r.data.message);
      setShowAdminDirectDlg(false);
      setAdText(''); setAdFile(null);
      loadStats(); loadVouchers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setAdBusy(false); }
  };

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get('/voucher-stock/stats');
      setStats(r.data);
    } catch (err) {
      console.error('Voucher stats load failed:', err);
    }
  }, []);

  const loadVouchers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterStaff) params.set('staff_id', filterStaff);
      if (filterVendor) params.set('vendor_id', filterVendor);
      if (search) params.set('search', search);
      const r = await api.get(`/voucher-stock/?${params.toString()}`);
      setVouchers(r.data || []);
      setSelected(new Set());
      // Seed rowAssignments with existing assignments
      const seed = {};
      (r.data || []).forEach((v) => {
        if (v.assigned_to_staff_id) seed[v.id] = { id: v.assigned_to_staff_id, name: v.assigned_to_staff_name };
      });
      setRowAssignments(seed);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterStaff, filterVendor, search]);

  const loadRefs = useCallback(async () => {
    try {
      const [st, dl] = await Promise.all([
        api.get('/voucher-stock/staff-list'),
        api.get('/voucher-stock/distributor-list'),
      ]);
      setStaffList(st.data || []);
      setDistributorList(dl.data || []);
    } catch (err) {
      console.error('Voucher refs load failed:', err);
    }
  }, []);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadVouchers(); }, [loadVouchers]);

  const toggleSelect = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const selectAll = () => {
    if (selected.size === vouchers.length) setSelected(new Set());
    else setSelected(new Set(vouchers.map((v) => v.id)));
  };

  // ═══ Drag-fill Handler (Google Sheets style) ═══
  const dragFillRef = useRef(null);
  const [dragPreview, setDragPreview] = useState(null); // { sourceId, targetIndex }

  const startDragFill = (sourceId, sourceIndex, e) => {
    e.preventDefault();
    e.stopPropagation();
    const assignment = rowAssignments[sourceId];
    if (!assignment || !assignment.id) {
      toast.error('Pehle is row ka staff select karo, phir drag karo');
      return;
    }
    dragFillRef.current = { sourceId, sourceIndex, assignment };

    const onMove = (ev) => {
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const row = el?.closest('[data-row-index]');
      if (row) {
        const idx = Number(row.getAttribute('data-row-index'));
        setDragPreview({ sourceIndex, targetIndex: idx, assignment });
      }
    };
    const onUp = (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const row = el?.closest('[data-row-index]');
      if (row && dragFillRef.current) {
        const endIdx = Number(row.getAttribute('data-row-index'));
        const { sourceIndex: sIdx, assignment: asn } = dragFillRef.current;
        const from = Math.min(sIdx, endIdx);
        const to = Math.max(sIdx, endIdx);
        setRowAssignments((prev) => {
          const next = { ...prev };
          for (let i = from; i <= to; i++) {
            const v = vouchers[i];
            if (v && (v.status === 'available' || v.status === 'assigned')) {
              next[v.id] = asn;
            }
          }
          return next;
        });
        toast.success(`${to - from + 1} rows me ${asn.name} auto-filled`);
      }
      setDragPreview(null);
      dragFillRef.current = null;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Clear all inline (unsaved) assignments — revert to server state
  const clearAllInline = () => {
    const reset = {};
    vouchers.forEach((v) => {
      if (v.assigned_to_staff_id) reset[v.id] = { id: v.assigned_to_staff_id, name: v.assigned_to_staff_name };
    });
    setRowAssignments(reset);
    toast.success('Unsaved inline assignments cleared');
  };

  // Clear single row's inline assignment
  const clearRowAssignment = (voucherId) => {
    const v = vouchers.find((x) => x.id === voucherId);
    setRowAssignments((p) => {
      const next = { ...p };
      if (v?.assigned_to_staff_id) {
        next[voucherId] = { id: v.assigned_to_staff_id, name: v.assigned_to_staff_name };
      } else {
        delete next[voucherId];
      }
      return next;
    });
  };

  // Apply row-level assignments (save to server)
  const saveRowAssignments = async () => {
    // Group by staff_id
    const byStaff = {};
    Object.entries(rowAssignments).forEach(([vid, asn]) => {
      if (!asn || !asn.id) return;
      const v = vouchers.find((x) => x.id === vid);
      if (!v) return;
      // Skip already-matching assignments
      if (v.assigned_to_staff_id === asn.id) return;
      if (!byStaff[asn.id]) byStaff[asn.id] = { name: asn.name, ids: [] };
      byStaff[asn.id].ids.push(vid);
    });
    const entries = Object.entries(byStaff);
    if (entries.length === 0) {
      toast.info('Koi naya assignment nahi hai');
      return;
    }
    try {
      for (const [staffId, info] of entries) {
        await api.post('/voucher-stock/assign', {
          voucher_ids: info.ids,
          staff_id: staffId,
          staff_name: info.name,
        });
      }
      toast.success(`${entries.reduce((s, [, i]) => s + i.ids.length, 0)} vouchers assigned`);
      await loadVouchers();
      await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Assign failed');
    }
  };

  // Bulk assign dialog
  const openBulkAssign = () => {
    if (selected.size === 0) { toast.error('Pehle vouchers select karo'); return; }
    setAssignStaff(null);
    setAssignStaffInput('');
    setShowAssignDlg(true);
  };

  const doBulkAssign = async () => {
    if (!assignStaff?.id) { toast.error('Staff select karo'); return; }
    try {
      const r = await api.post('/voucher-stock/assign', {
        voucher_ids: Array.from(selected),
        staff_id: assignStaff.id,
        staff_name: assignStaff.name,
      });
      toast.success(r.data.message);
      setShowAssignDlg(false);
      await loadVouchers();
      await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Assign failed');
    }
  };

  // Sell dialog
  const openSellDlg = () => {
    if (selected.size === 0) { toast.error('Pehle vouchers select karo'); return; }
    setSellForm({ sold_to_name: '', sold_to_distributor_id: '' });
    setShowSellDlg(true);
  };

  const doMarkSold = async () => {
    if (!sellForm.sold_to_name && !sellForm.sold_to_distributor_id) {
      toast.error('Customer name ya distributor select karo'); return;
    }
    try {
      const r = await api.post('/voucher-stock/sell', {
        voucher_ids: Array.from(selected),
        sold_to_name: sellForm.sold_to_name,
        sold_to_distributor_id: sellForm.sold_to_distributor_id || null,
      });
      toast.success(r.data.message);
      setShowSellDlg(false);
      await loadVouchers(); await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sell failed');
    }
  };

  // Complaint
  const openComplaintDlg = (voucher) => {
    setComplaintVoucher(voucher);
    setComplaintForm({ complaint_type: 'used', details: '' });
    setShowComplaintDlg(true);
  };
  const doRaiseComplaint = async () => {
    try {
      await api.post('/voucher-stock/complaints', {
        voucher_id: complaintVoucher.id,
        complaint_type: complaintForm.complaint_type,
        details: complaintForm.details,
      });
      toast.success('Complaint raised');
      setShowComplaintDlg(false);
      await loadVouchers(); await loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const statusChips = [
    { key: 'available', label: 'Available', color: 'emerald' },
    { key: 'assigned', label: 'Assigned', color: 'blue' },
    { key: 'sold', label: 'Sold', color: 'indigo' },
    { key: 'complaint', label: 'Complaint', color: 'amber' },
    { key: 'reversed', label: 'Reversed', color: 'red' },
    { key: '', label: 'All', color: 'gray' },
  ];

  const rowAssignmentCount = useMemo(() => {
    let changed = 0;
    vouchers.forEach((v) => {
      const a = rowAssignments[v.id];
      if (a?.id && a.id !== v.assigned_to_staff_id) changed++;
    });
    return changed;
  }, [vouchers, rowAssignments]);

  return (
    <div className="space-y-5" data-testid="voucher-stock-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Gift size={26} weight="duotone" className="text-fuchsia-600" /> Voucher Stock
        </h1>
        <span className="text-sm text-gray-500">Admin vouchers assign, sell aur complaints track karein</span>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAdminDirectDlg(true)} className="bg-fuchsia-50 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-100" data-testid="admin-direct-upload-btn">
            <UserPlus size={14} /> Admin Direct Upload
          </Button>
          <Button variant="outline" size="sm" onClick={() => { loadStats(); loadVouchers(); }}>
            <ArrowClockwise size={14} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={<Package size={18} />} label="Admin Stock (Available)"
            count={stats.by_status.available.count} face={stats.by_status.available.face_total}
            color="emerald" />
          <StatCard icon={<UsersThree size={18} />} label="Assigned to Staff"
            count={stats.by_status.assigned.count} face={stats.by_status.assigned.face_total}
            color="blue" />
          <StatCard icon={<Storefront size={18} />} label="Sold"
            count={stats.by_status.sold.count} face={stats.by_status.sold.face_total}
            color="indigo" />
          <StatCard icon={<Warning size={18} />} label="Complaint"
            count={stats.by_status.complaint.count} face={stats.by_status.complaint.face_total}
            color="amber" />
          <StatCard icon={<Warning size={18} />} label="Reversed"
            count={stats.by_status.reversed.count} face={stats.by_status.reversed.face_total}
            color="red" />
        </div>
      )}

      {/* Per-vendor breakdown */}
      {stats?.per_vendor?.length > 0 && (
        <div className="bg-white border rounded-xl p-4" data-testid="per-vendor-breakdown">
          <div className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Storefront size={18} className="text-fuchsia-600" /> Per-Vendor Stock Breakdown ({stats.per_vendor.length} vendors)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right text-emerald-700">Available</th>
                  <th className="px-3 py-2 text-right text-blue-700">Assigned</th>
                  <th className="px-3 py-2 text-right text-indigo-700">Sold</th>
                  <th className="px-3 py-2 text-right text-amber-700">Complaint</th>
                  <th className="px-3 py-2 text-right text-red-700">Reversed</th>
                  <th className="px-3 py-2 text-center">Filter</th>
                </tr>
              </thead>
              <tbody>
                {stats.per_vendor.map((v) => (
                  <tr key={v.vendor_id} className={`border-t hover:bg-fuchsia-50/40 ${filterVendor === v.vendor_id ? 'bg-fuchsia-50' : ''}`} data-testid={`vendor-row-${v.vendor_id}`}>
                    <td className="px-3 py-2 font-semibold">{v.vendor_name}</td>
                    <td className="px-3 py-2 text-right font-bold">{v.total_count} <span className="text-[10px] text-gray-500">/ {fmt(v.total_face)}</span></td>
                    <td className="px-3 py-2 text-right text-emerald-800">{v.available_count} <span className="text-[10px]">/ {fmt(v.available_face)}</span></td>
                    <td className="px-3 py-2 text-right text-blue-800">{v.assigned_count} <span className="text-[10px]">/ {fmt(v.assigned_face)}</span></td>
                    <td className="px-3 py-2 text-right text-indigo-800">{v.sold_count} <span className="text-[10px]">/ {fmt(v.sold_face)}</span></td>
                    <td className="px-3 py-2 text-right text-amber-800">{v.complaint_count} <span className="text-[10px]">/ {fmt(v.complaint_face)}</span></td>
                    <td className="px-3 py-2 text-right text-red-800">{v.reversed_count} <span className="text-[10px]">/ {fmt(v.reversed_face)}</span></td>
                    <td className="px-3 py-2 text-center">
                      <Button size="sm" variant={filterVendor === v.vendor_id ? 'default' : 'outline'} className="h-6 text-[10px] px-2"
                        onClick={() => setFilterVendor(filterVendor === v.vendor_id ? '' : v.vendor_id)}
                        data-testid={`filter-vendor-${v.vendor_id}`}>
                        {filterVendor === v.vendor_id ? '✓ Filtering' : 'Filter'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per-staff allocation */}
      {stats?.per_staff?.length > 0 && (
        <div className="bg-white border rounded-xl p-4" data-testid="per-staff-allocation">
          <div className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <UsersThree size={18} className="text-blue-600" /> Per-Staff Allocation
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.per_staff.map((s) => (
              <div key={s.staff_id} className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer" onClick={() => setFilterStaff(s.staff_id)}>
                <div className="font-semibold text-gray-900 text-sm">{s.staff_name}</div>
                <div className="text-xs text-gray-600 mt-1">
                  Total: <span className="font-semibold">{s.total_count} vouchers / {fmt(s.total_face)}</span>
                </div>
                <div className="flex gap-2 mt-2 text-[11px]">
                  <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">Pending: {s.available_count} / {fmt(s.available_face)}</span>
                  <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">Sold: {s.sold_count} / {fmt(s.sold_face)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border rounded-lg p-3 flex flex-wrap gap-2 items-center">
        <div className="flex gap-1 flex-wrap">
          {statusChips.map((c) => (
            <button
              key={c.key}
              onClick={() => setFilterStatus(c.key)}
              className={`px-3 py-1 text-xs font-semibold rounded border transition ${
                filterStatus === c.key
                  ? `bg-${c.color}-600 border-${c.color}-600 text-white`
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`filter-${c.key || 'all'}`}
            >{c.label}</button>
          ))}
        </div>
        <div className="ml-auto flex-1 md:max-w-xs">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Code / vendor / staff search..."
            matchCount={vouchers.length}
            noun="vouchers"
            testId="voucher-search"
            inputClassName="pl-8 pr-7 h-9 text-sm"
          />
        </div>
        {filterVendor && stats?.per_vendor && (
          <Button variant="outline" size="sm" onClick={() => setFilterVendor('')}>
            Vendor filter clear ({stats.per_vendor.find((v) => v.vendor_id === filterVendor)?.vendor_name || '...'})
          </Button>
        )}
        {filterStaff && (
          <Button variant="outline" size="sm" onClick={() => setFilterStaff('')}>
            Staff filter clear ({staffList.find((s) => s.id === filterStaff)?.name || '...'})
          </Button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg p-3 sticky top-0 z-30" data-testid="voucher-toolbar">
        <span className="text-xs font-semibold text-indigo-900">
          {selected.size} selected / {vouchers.length} shown
        </span>
        <Button size="sm" variant="outline" onClick={selectAll} data-testid="select-all-btn">
          <CheckSquare size={14} /> {selected.size === vouchers.length && vouchers.length > 0 ? 'Unselect All' : 'Select All'}
        </Button>
        {selected.size > 0 && (
          <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => setSelected(new Set())} data-testid="clear-selection-btn">
            Clear Selection
          </Button>
        )}
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1" onClick={openBulkAssign} data-testid="bulk-assign-btn">
          <UserPlus size={14} /> Assign to Staff ({selected.size})
        </Button>
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 gap-1" onClick={openSellDlg} data-testid="bulk-sell-btn">
          <ShoppingBag size={14} /> Mark as Sold ({selected.size})
        </Button>
        {rowAssignmentCount > 0 && (
          <>
            <Button size="sm" variant="outline" className="text-red-600 border-red-300 ml-auto" onClick={clearAllInline} data-testid="clear-all-inline-btn">
              Clear {rowAssignmentCount} Unsaved Changes
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1" onClick={saveRowAssignments} data-testid="save-assignments-btn">
              <CheckSquare size={14} /> Save {rowAssignmentCount} Inline Assignments
            </Button>
          </>
        )}
      </div>

      {/* Vouchers Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        ) : vouchers.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">No vouchers found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500 sticky top-0">
                <tr>
                  <th className="w-10 px-2 py-2"><input type="checkbox" checked={selected.size === vouchers.length && vouchers.length > 0} onChange={selectAll} /></th>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-left">Pin</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left min-w-[200px]">Assigned To (type name — drag ↓ to fill down)</th>
                  <th className="px-3 py-2 text-left">Sold To</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v, idx) => {
                  const asn = rowAssignments[v.id];
                  const inDragRange = dragPreview && Math.min(dragPreview.sourceIndex, dragPreview.targetIndex) <= idx && idx <= Math.max(dragPreview.sourceIndex, dragPreview.targetIndex);
                  const canAssign = v.status === 'available' || v.status === 'assigned';
                  return (
                    <tr key={v.id} data-row-index={idx}
                      className={`border-t hover:bg-gray-50 ${selected.has(v.id) ? 'bg-indigo-50/50' : ''} ${inDragRange ? 'bg-yellow-100/70' : ''}`}
                      data-testid={`voucher-row-${v.id}`}>
                      <td className="px-2 py-2 align-middle">
                        <input type="checkbox" checked={selected.has(v.id)} onChange={() => toggleSelect(v.id)} />
                      </td>
                      <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                      <td className="px-3 py-2 font-mono text-xs">{v.code}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-500">{v.pin || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmt(v.face_value)}</td>
                      <td className="px-3 py-2 text-xs">{v.vendor_name}</td>
                      <td className="px-3 py-2"><StatusBadge status={v.status} /></td>
                      <td className="px-3 py-2 relative">
                        {canAssign ? (
                          <div className="flex items-center gap-1">
                            <StaffAutocomplete
                              staffList={staffList}
                              value={asn}
                              onChange={(s) => setRowAssignments((p) => ({ ...p, [v.id]: s }))}
                              disabled={v.status === 'sold' || v.status === 'complaint' || v.status === 'reversed'}
                            />
                            {/* Clear row assignment button — shown if inline differs from saved */}
                            {asn?.id && asn.id !== v.assigned_to_staff_id && (
                              <button
                                type="button"
                                className="w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-sm flex items-center justify-center text-xs font-bold"
                                onClick={() => clearRowAssignment(v.id)}
                                title="Clear unsaved assignment"
                                data-testid={`clear-row-${v.id}`}
                              >
                                ×
                              </button>
                            )}
                            {/* Drag-fill handle */}
                            {asn?.id && (
                              <button
                                type="button"
                                className="w-5 h-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm flex items-center justify-center cursor-crosshair"
                                onMouseDown={(e) => startDragFill(v.id, idx, e)}
                                title="Drag down to fill staff below"
                                data-testid={`drag-fill-${v.id}`}
                              >
                                <ArrowDown size={10} weight="bold" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">{v.assigned_to_staff_name || '—'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">{v.sold_to_name || v.sold_to_distributor_name || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        {!v.has_complaint && v.status !== 'reversed' && (
                          <Button size="sm" variant="outline" className="text-red-600 h-7 px-2" onClick={() => openComplaintDlg(v)} data-testid={`complaint-${v.id}`}>
                            <Siren size={12} /> Complaint
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk Assign Dialog */}
      <Dialog open={showAssignDlg} onOpenChange={setShowAssignDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign {selected.size} Vouchers to Staff</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <StaffAutocomplete
              staffList={staffList}
              value={assignStaff}
              textValue={assignStaffInput}
              onTextChange={setAssignStaffInput}
              onChange={(s) => { setAssignStaff(s); setAssignStaffInput(s?.name || ''); }}
              placeholder="Staff name type karo (e.g. Ram Bali)..."
              autoFocus
            />
            <p className="text-xs text-gray-500">Google Sheets jaise type karo — suggestions automatic aaenge.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDlg(false)}>Cancel</Button>
            <Button onClick={doBulkAssign} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-assign-btn" disabled={!assignStaff?.id}>
              Assign {selected.size} vouchers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sell Dialog */}
      <Dialog open={showSellDlg} onOpenChange={setShowSellDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark {selected.size} Vouchers as Sold</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Customer Name (free-text)</label>
              <Input placeholder="e.g. Mr. Sharma / Shop Name" value={sellForm.sold_to_name}
                onChange={(e) => setSellForm({ ...sellForm, sold_to_name: e.target.value })} />
            </div>
            <div className="text-center text-xs text-gray-500">— OR —</div>
            <div>
              <label className="text-xs font-semibold">Select Distributor (optional)</label>
              <select className="w-full h-10 px-3 border rounded-md text-sm" value={sellForm.sold_to_distributor_id}
                onChange={(e) => setSellForm({ ...sellForm, sold_to_distributor_id: e.target.value })}>
                <option value="">— No distributor —</option>
                {distributorList.map((d) => (
                  <option key={d.id} value={d.id}>{d.name} {d.agency_name ? `(${d.agency_name})` : ''}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSellDlg(false)}>Cancel</Button>
            <Button onClick={doMarkSold} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-sell-btn">
              Mark Sold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complaint Dialog */}
      <Dialog open={showComplaintDlg} onOpenChange={setShowComplaintDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Raise Complaint</DialogTitle></DialogHeader>
          {complaintVoucher && (
            <div className="space-y-3">
              <div className="text-xs p-2 bg-gray-50 rounded">
                <div><span className="text-gray-500">Code:</span> <span className="font-mono">{complaintVoucher.code}</span></div>
                <div><span className="text-gray-500">Amount:</span> <span className="font-semibold">{fmt(complaintVoucher.face_value)}</span></div>
                <div><span className="text-gray-500">Vendor:</span> {complaintVoucher.vendor_name}</div>
              </div>
              <div>
                <label className="text-xs font-semibold">Complaint Type *</label>
                <select className="w-full h-10 px-3 border rounded-md text-sm" value={complaintForm.complaint_type}
                  onChange={(e) => setComplaintForm({ ...complaintForm, complaint_type: e.target.value })}>
                  <option value="used">Already Used</option>
                  <option value="invalid">Invalid Code</option>
                  <option value="expired">Expired</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold">Details</label>
                <textarea rows={3} className="w-full px-3 py-2 border rounded-md text-sm" placeholder="Complaint ka details..."
                  value={complaintForm.details} onChange={(e) => setComplaintForm({ ...complaintForm, details: e.target.value })} />
              </div>
              <div className="text-[11px] text-amber-700 bg-amber-50 px-2 py-1.5 rounded">
                ℹ️ Resolve karne pe vendor ka ledger {fmt(complaintVoucher.credited_amount)} se debit ho jayega.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplaintDlg(false)}>Cancel</Button>
            <Button onClick={doRaiseComplaint} className="bg-red-600 hover:bg-red-700" data-testid="confirm-complaint-btn">
              Raise Complaint
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin-Direct Upload Dialog */}
      <Dialog open={showAdminDirectDlg} onOpenChange={setShowAdminDirectDlg}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={18} className="text-fuchsia-600" /> Admin Direct Upload
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-fuchsia-50 border border-fuchsia-200 rounded p-3 text-sm text-fuchsia-900">
              <b>Use case:</b> Vendor invoice nahi, admin ne khud bazaar se gift cards kharide.
              <ul className="list-disc ml-5 mt-1 text-xs">
                <li>Auto-vendor "Admin Direct Stock" me jate hain (synthetic, no ledger credit)</li>
                <li>Phir same flow se staff/sub-staff ko assign kar sakte ho</li>
                <li>Discount = 0% — full face value admin ka own stock</li>
              </ul>
            </div>
            <div className="flex gap-2 text-xs">
              <button onClick={() => setAdMode('grouped')} className={`px-3 py-1.5 rounded border font-medium ${adMode === 'grouped' ? 'bg-fuchsia-600 text-white border-fuchsia-700' : 'bg-white text-gray-700 border-gray-300'}`} data-testid="ad-mode-grouped">1. Grouped</button>
              <button onClick={() => setAdMode('line')} className={`px-3 py-1.5 rounded border font-medium ${adMode === 'line' ? 'bg-fuchsia-600 text-white border-fuchsia-700' : 'bg-white text-gray-700 border-gray-300'}`} data-testid="ad-mode-line">2. Per Line</button>
              <label className={`px-3 py-1.5 rounded border font-medium cursor-pointer ${adFile ? 'bg-fuchsia-600 text-white border-fuchsia-700' : 'bg-white text-gray-700 border-gray-300'}`}>
                3. Excel
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => setAdFile(e.target.files?.[0] || null)} data-testid="ad-file-input" />
              </label>
              {adFile && <span className="text-[11px] text-gray-600 self-center truncate">{adFile.name} <button onClick={() => setAdFile(null)} className="text-red-600 ml-1">×</button></span>}
            </div>
            {!adFile && (
              <textarea
                value={adText}
                onChange={(e) => setAdText(e.target.value)}
                placeholder={adMode === 'grouped'
                  ? `5000:\nCODE-A1\nCODE-A2\n\n2500:\nCODE-B1`
                  : `CODE-A1\t5000\nCODE-A2\t5000\nCODE-B1\t2500`}
                rows={10}
                className="w-full border rounded p-2 font-mono text-xs"
                data-testid="ad-text-input"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminDirectDlg(false)} disabled={adBusy}>Cancel</Button>
            <Button onClick={submitAdminDirect} disabled={adBusy} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="confirm-admin-direct-btn">
              {adBusy ? 'Uploading…' : 'Upload to Admin Stock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, count, face, color }) {
  const bg = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
  }[color] || 'bg-gray-50 border-gray-200 text-gray-700';
  return (
    <div className={`${bg} border rounded-lg p-3`}>
      <div className="flex items-center gap-1 text-[11px] uppercase font-semibold">{icon} {label}</div>
      <div className="text-lg font-bold mt-1">{count}</div>
      <div className="text-[10px] opacity-75">{fmt(face)}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    available: 'bg-emerald-100 text-emerald-700',
    assigned: 'bg-blue-100 text-blue-700',
    sold: 'bg-indigo-100 text-indigo-700',
    complaint: 'bg-amber-100 text-amber-700',
    reversed: 'bg-red-100 text-red-700',
  };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status?.toUpperCase()}</span>;
}

/** Inline autocomplete input for staff selection — Google Sheets style */
function StaffAutocomplete({ staffList, value, onChange, placeholder = 'Type name...', textValue, onTextChange, autoFocus, disabled }) {
  const [text, setText] = useState(value?.name || '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const controlled = textValue !== undefined;
  const txt = controlled ? textValue : text;

  useEffect(() => {
    if (!controlled) setText(value?.name || '');
  }, [value, controlled]);

  const suggestions = useMemo(() => {
    if (!txt) return staffList.slice(0, 8);
    const q = txt.toLowerCase();
    return staffList.filter((s) => s.name.toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q)).slice(0, 8);
  }, [txt, staffList]);

  const handleSelect = (s) => {
    if (onChange) onChange(s);
    if (controlled && onTextChange) onTextChange(s.name); else setText(s.name);
    setOpen(false);
  };

  const update = (t) => {
    if (controlled && onTextChange) onTextChange(t); else setText(t);
    setHighlight(0);
    setOpen(true);
    // Clear selection if text changed
    if (onChange && value?.name !== t) {
      const match = staffList.find((s) => s.name === t);
      if (match) onChange(match);
      else if (value) onChange(null);
    }
  };

  return (
    <div className="relative w-full">
      <input
        type="text"
        disabled={disabled}
        className="w-full h-8 px-2 border rounded text-xs focus:ring-1 focus:ring-indigo-400 focus:outline-none disabled:bg-gray-100"
        placeholder={placeholder}
        value={txt}
        onChange={(e) => update(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (suggestions[highlight]) handleSelect(suggestions[highlight]); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        autoFocus={autoFocus}
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white border rounded shadow-lg max-h-52 overflow-y-auto">
          {suggestions.map((s, i) => (
            <div
              key={s.id}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
              className={`px-2 py-1.5 text-xs cursor-pointer ${i === highlight ? 'bg-indigo-100' : 'hover:bg-gray-50'}`}
            >
              <div className="font-semibold">{s.name}</div>
              <div className="text-[10px] text-gray-500">{s.email} • {s.role}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
